import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import brand from '@brand/brand.json';

/**
 * Routing.
 *
 * The canonical shape is `/:orgSlug/:pageSlug` because this is a multi-tenant
 * scheduler. There is no marketing landing page: the root goes straight to the
 * booking form, since every visitor arrives with one intent.
 *
 * Which page the root resolves to is configuration, not a hardcoded route, so
 * a second event or a second tenant is an env change rather than a code change.
 */
// The tenant this build serves. Taken from the brand manifest so a build can
// never be branded for one tenant while pointing at another's data.
const DEFAULT_ORG = brand.orgSlug;
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: { name: 'booking-page', params: { orgSlug: DEFAULT_ORG } },
  },
  {
    // The canonical public URL: /humentra, with no page segment. The API
    // resolves the tenant's default published page, so a second brand is
    // reachable at /aslase without any routing change.
    path: '/:orgSlug',
    name: 'booking-page',
    component: () => import('@/pages/BookingPage.vue'),
    props: true,
  },
  {
    // Still addressable per page, for a tenant running more than one event.
    path: '/:orgSlug/:pageSlug',
    name: 'booking-page-named',
    component: () => import('@/pages/BookingPage.vue'),
    props: true,
  },
  {
    path: '/bookings/:token',
    name: 'manage-booking',
    component: () => import('@/pages/ManageBookingPage.vue'),
    props: true,
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/pages/AdminPage.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFoundPage.vue'),
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
});
